import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Provider } from 'react-redux';
import { IconRegistry } from '@tylertech/forge';
import { tylIconThumbUpAlt, tylIconThumbDownAlt, tylIconThumbUpOffAlt, tylIconThumbDownOffAlt, tylIconCode } from '@tylertech/tyler-icons/standard';
import { tylIconFileSearchOutline, tylIconChartBar } from '@tylertech/tyler-icons/extended';
import './css/index.scss';
import reduxStore from './redux';
import ChatBotComponent from './components/chatBotComponent/ChatBotComponentContainer';
import ChatBotButton from './components/chatBotButton/ChatbotButton';
import { fetchConfiguration } from './redux/actions/appConfiguration';
import { addInitialVifPrompt } from './redux/actions/messages';
import { useAppDispatch } from './redux';
import { Mode, ProxyApp, convertFromExternalAppContextToInternal, ActionFunctionTypes, isSoql, RenderMode, isOmni, isVif, } from './types';
import { getComponentOpenState, updateComponentOpenState } from './components/utils/localStorage';
IconRegistry.define([
    tylIconThumbUpAlt,
    tylIconThumbUpOffAlt,
    tylIconThumbDownAlt,
    tylIconThumbDownOffAlt,
    tylIconCode,
    tylIconFileSearchOutline,
    tylIconChartBar,
]);
const AiChatBotComponent = ({ mode, renderMode, proxyApp = ProxyApp.FRONTEND, appContext, actionFunction, fetchAppConfig, setInitialVifPrompt, }) => {
    const [isOpenState, setIsOpenState] = useState(false);
    useEffect(() => {
        fetchAppConfig();
        if (isVif(mode)) {
            setInitialVifPrompt();
        }
    }, []);
    const calculatedCommonQuestions = useMemo(() => {
        if (isSoql(mode) || isOmni(mode)) {
            const commonQuestions = appContext.commonQuestions;
            if (commonQuestions && commonQuestions.length > 0) {
                return commonQuestions;
            }
        }
        return undefined;
    }, []);
    const calculatedRenderMode = useMemo(() => {
        if (renderMode) {
            return renderMode;
        }
        switch (mode) {
            case Mode.SOQL:
                return RenderMode.FLOATING;
            case Mode.VIF:
                return RenderMode.FIXED;
            case Mode.CATALOG:
                return RenderMode.FLOATING;
            case Mode.OMNI:
                return RenderMode.FULL_SCREEN;
            default:
                return RenderMode.FLOATING;
        }
    }, [renderMode]);
    useEffect(() => {
        if (calculatedRenderMode === RenderMode.FIXED || calculatedRenderMode === RenderMode.FULL_SCREEN) {
            setIsOpenState(true);
        }
        else if (calculatedRenderMode === RenderMode.FLOATING) {
            const savedOpenState = getComponentOpenState();
            setIsOpenState(savedOpenState);
        }
    }, [calculatedRenderMode]);
    const onCloseButtonClick = () => {
        setIsOpenState(false);
        updateComponentOpenState(false);
    };
    const onOpenButtonClick = () => {
        setIsOpenState(true);
        updateComponentOpenState(true);
    };
    const modeDependentStyling = classNames('chat-btn-layout', {
        'floating-layout': calculatedRenderMode === RenderMode.FLOATING,
        'fixed-layout': calculatedRenderMode === RenderMode.FIXED,
        'full-screen-layout': calculatedRenderMode === RenderMode.FULL_SCREEN,
        'component-is-open': isOpenState && (calculatedRenderMode === RenderMode.FLOATING || calculatedRenderMode === RenderMode.FULL_SCREEN),
    });
    const convertedAppContext = (() => {
        try {
            return convertFromExternalAppContextToInternal(appContext, mode);
        }
        catch (error) {
            console.error(error);
            return null;
        }
    })();
    if (!convertedAppContext) {
        return null;
    }
    return (React.createElement("div", { className: modeDependentStyling }, isOpenState ? (React.createElement(ChatBotComponent, { mode: mode, renderMode: calculatedRenderMode, proxyApp: proxyApp, appContext: convertedAppContext, actionFunction: actionFunction, updateBotOpenState: () => onCloseButtonClick(), commonQuestions: calculatedCommonQuestions })) : (React.createElement(ChatBotButton, { onChatBotButtonClick: onOpenButtonClick }))));
};
const AiChatBotComponentContainer = props => {
    const { proxyApp = ProxyApp.FRONTEND } = props;
    const appDispatch = useAppDispatch();
    const propsWithRedux = Object.assign(Object.assign({}, props), { fetchAppConfig: () => appDispatch(fetchConfiguration({ proxyApp: proxyApp })), setInitialVifPrompt: () => appDispatch(addInitialVifPrompt()) });
    return React.createElement(AiChatBotComponent, Object.assign({}, propsWithRedux));
};
const ReduxWrapper = props => {
    return (React.createElement(Provider, { store: reduxStore },
        React.createElement(AiChatBotComponentContainer, Object.assign({}, props))));
};
export default ReduxWrapper;
export { Mode, RenderMode, ProxyApp, ActionFunctionTypes };
//# sourceMappingURL=index.js.map